import React, { useEffect } from 'react';
import useModal from '@/hooks/useModal';
import { Button, Spinner, Typography } from '@/components/common';
import { Textarea } from '@/components/form';
import { FieldValues, useForm } from 'react-hook-form';
import { ModalHeader } from '../../modals/ModalHeader';
import { useQuery } from '@tanstack/react-query';
import {
	actionRequiredService,
	GET_FOLLOW_UP_ANSWERS,
	GET_QUESTIONS_FOLLOWUP,
} from '@/api';
import { FollowUAnswersPayload } from '@/interfaces';
import { useNotification } from '@/hooks';
import { formatErrorMessage } from '@/lib';
import { caseApplicantService } from '@/api/caseApplicant';
import { InfoCircle } from '@/public/icons/tsx';

interface Props {
	actionRequiredId: number;
	isOpen: boolean;
	setIsModalOpen: (value: boolean) => void;
	refetch?: () => void;
	isFollowUpResponsed?: boolean;
	modalTitle?: string;
}

export const ModalFollowUp: React.FC<Props> = ({
	actionRequiredId,
	isOpen,
	setIsModalOpen,
	refetch,
	isFollowUpResponsed,
	modalTitle,
}) => {
	const { Modal, show, hide } = useModal();
	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm();
	const { toastSuccess, toastError } = useNotification();

	const { data: followUpQuestions } = useQuery({
		queryKey: [GET_QUESTIONS_FOLLOWUP, actionRequiredId],
		queryFn: () => actionRequiredService.getFollowUpQuestions(actionRequiredId),
		enabled: !!actionRequiredId,
		gcTime: 0,
	});

	const { data: followUpAnswers } = useQuery({
		queryKey: [GET_FOLLOW_UP_ANSWERS, actionRequiredId],
		queryFn: () => caseApplicantService.getFollowUpById(actionRequiredId),
		enabled: !!actionRequiredId,
	});

	useEffect(() => {
		if (isOpen) {
			show();
			reset();
		}
	}, [isOpen]);

	const handleCloseModal = (): void => {
		hide();
		setIsModalOpen(false);
	};

	const handleSubmitAnswers = async (data: FieldValues): Promise<void> => {
		try {
			const questionsId = Object.keys(data);
			const answers = Object.values(data);
			const newData: FollowUAnswersPayload = {
				action_id: actionRequiredId,
				answers: answers.map((item: string, index) => {
					return { answer: item, form_id: Number(questionsId[index]) };
				}),
			};
			await actionRequiredService.putFollowUpAnswers(newData);
			refetch && refetch();
			toastSuccess('Follow-up required has been successfully completed.');
			handleCloseModal();
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		}
	};

	return (
		<Modal
			classNameCardContainer="!bg-gradient-to-r from-white to-white lg:min-w-[725px] max-w-2xl"
			withXButton
			title={
				<ModalHeader
					title={
						isFollowUpResponsed && modalTitle
							? modalTitle
							: 'Follow up required'
					}
				/>
			}
			onClose={handleCloseModal}
		>
			<form className="space-y-10" onSubmit={handleSubmit(handleSubmitAnswers)}>
				{followUpQuestions ? (
					<ul className="space-y-8">
						{!isFollowUpResponsed &&
							followUpQuestions?.data.map((question, index) => (
								<li key={'follow-up-question' + question.id}>
									<Typography type="semibold-16" className="text-left">
										Question {index + 1}:
									</Typography>
									<Typography type="regular-16" className="text-left">
										{question.question}
									</Typography>
									<Textarea
										{...register(question.id.toString(), {
											required: 'This field is required',
										})}
										name={question.id.toString()}
										id={question.id.toString()}
										placeholder="Write your response here"
										register={register}
										classNameInput="!rounded-[32px] placeholder:opacity-60"
										rows={3}
									/>
									{errors?.[question.id.toString()] && (
										<div className="flex items-center gap-x-1.5">
											<InfoCircle fill="#FF0043" width="16" height="16" />
											<span className="flex items-center mt-1 gap-1">
												<Typography type="regular-12" className="text-main-red">
													{errors?.[
														question?.id?.toString()
													]?.message?.toString() ?? ''}
												</Typography>
											</span>
										</div>
									)}
								</li>
							))}

						{isFollowUpResponsed &&
							followUpAnswers?.data.form.map((question, index) => (
								<li key={'follow-up-question-' + index}>
									<Typography type="semibold-16" className="text-left">
										Question {index + 1}:
									</Typography>
									<Typography type="regular-16" className="text-left">
										{question.question}
									</Typography>
									<div className="rounded-[32px] bg-white border border-grey-100 px-6 py-4 min-h-[90px] mt-2">
										<Typography
											type="regular-16"
											className="text-left text-opacity-black"
										>
											<span className="font-semibold">Client’s answer:</span>{' '}
											{question.anwser?.split('\n').map((line, index) => (
												<React.Fragment key={index}>
													{line}
													<br />
												</React.Fragment>
											))}
										</Typography>
									</div>
								</li>
							))}
					</ul>
				) : (
					<div className="my-6">
						<Spinner type="loadingPage" className="m-auto" />
					</div>
				)}

				{!isFollowUpResponsed && (
					<div className="flex justify-center">
						<Button
							type="submit"
							size="medium"
							decoration="primary"
							className="flex items-center gap-2 !py-4 !w-40"
						>
							Reply
						</Button>
					</div>
				)}
			</form>
		</Modal>
	);
};
